import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@common/components'
import { useAppDispatch, useToggle } from '@common/hooks'
import errorHandler from '@common/utils/error-handler'
import parse from 'html-react-parser'
import { ModalDisplayNote } from './modal-display-note'
import {
  ClassroomTaskResponse,
  useClassroomGetCurrentClassroomQuery,
  useClassroomGetIspeakTasksQuery,
  useClassroomGetLivePracticeTaskQuery,
  useClassroomGetTeacherTaskFoldersQuery,
  useClassroomListClassroomTasksQuery,
  useClassroomOrderClassroomTasksMutation,
} from '@app/services/auto-generated/classroom'
import { MenuNote } from './menu-note'
import React from 'react'
import MenuIspeakNote from './menu-ispeak-note'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AddCircleOutlineOutlinedIcon } from '@common/icons'
import CreateFolderModal from '../create-folder-modal'
import { FolderButton } from './display-notes.styles'
import TaskCarouselView from '../task-carousel-view'
import Task from '../task'
import { classroomApi } from '@app/services'

const DisplayNotes = (props) => {
  const { setDisplayPoppover, setFolder, setDisplayFolderPoppover } = props
  const { data: currentClassroom } = useClassroomGetCurrentClassroomQuery()
  const {
    data: tasks,
    isLoading,
    isError,
    error,
    isSuccess,
  } = useClassroomListClassroomTasksQuery({ classroomId: currentClassroom?.id })
  const {
    data: ispeakTasks,
    isLoading: isLoadingIspeak,
    isError: isErrorIspeak,
    error: errorIspeak,
    isSuccess: isSuccessIspeak,
  } = useClassroomGetIspeakTasksQuery({ classroomId: currentClassroom?.id })
  const [activeButton, setActiveButton] = React.useState('my-tasks')
  const {
    data: livePracticeTask,
    isLoading: isLoadinglivePractice,
    isError: isErrorlivePractice,
    error: errorlivePractice,
    isSuccess: isSuccesslivePractice,
  } = useClassroomGetLivePracticeTaskQuery()

  const handleButtonClick = (buttonType) => {
    setActiveButton(buttonType)
  }
  const { open, handleOpen, handleClose } = useToggle(false)
  const { data: folders } = useClassroomGetTeacherTaskFoldersQuery()
  const [selectedFolder, setSelectedFolder] = React.useState()
  const [folderIndex, setFolderIndex] = React.useState()
  React.useEffect(() => {
    if (selectedFolder) {
      setSelectedFolder(folders[folderIndex])
    }
  }, [folders])

  React.useEffect(() => {
    setActiveButton('my-tasks')
  }, [folders?.length])

  const [studendView, setStudentView] = React.useState(false)

  const dispatch = useAppDispatch()

  const handleUpdateCache = (queryCacheName: string, orderArray: any) => {
    dispatch(
      classroomApi.util.updateQueryData(
        queryCacheName,
        { classroomId: currentClassroom?.id },
        (draft) => {
          const idIndexMap = orderArray.reduce((acc, curr, index) => {
            acc[curr.item.id] = index
            return acc
          }, {})
          const newDraft = draft.sort((a, b) => {
            const indexA = idIndexMap[a.id]
            const indexB = idIndexMap[b.id]
            return indexA - indexB
          })
          return newDraft
        },
      ),
    )
  }
  const handleUpdateCacheFolder = (queryCacheName: string, orderArray: any) => {
    dispatch(
      classroomApi.util.updateQueryData(queryCacheName, undefined, (draft) => {
        const idIndexMap = orderArray.reduce((acc, curr, index) => {
          acc[curr.item.id] = index
          return acc
        }, {})
        if (folderIndex >= 0) {
          const newTasks = draft[folderIndex]?.tasks.sort((a, b) => {
            const indexA = idIndexMap[a.id]
            const indexB = idIndexMap[b.id]
            return indexA - indexB
          })
          draft[folderIndex].tasks = newTasks
          return draft
        }
      }),
    )
  }

  return (
    <>
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' sx={{ mb: 2 }}>
        <Grid container spacing={1}>
          {folders?.map((folder, index) => {
            return (
              <Grid item key={index}>
                <FolderButton
                  active={activeButton === folder?.name}
                  onClick={() => {
                    handleButtonClick(folder.name)
                    setSelectedFolder(folder)
                    setFolder(folder)
                    setDisplayPoppover(true)
                    setFolderIndex(index)
                    setDisplayFolderPoppover(true)
                  }}
                >
                  {folder?.name}
                </FolderButton>
              </Grid>
            )
          })}
        </Grid>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems='center'
          spacing={0.5}
          sx={{ pt: 1 }}
        >
          <Switch
            value={studendView}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setStudentView(event.target.checked)
            }}
          />
          <Typography component={'span'} variant='caption' noWrap>
            Student view
          </Typography>
        </Stack>
      </Stack>
      {activeButton === 'my-tasks' && (
        <>
          {isLoading ? (
            Array(3)
              .fill(null)
              .map((elem, index) => <Skeleton height={160} key={index} />)
          ) : isError ? (
            <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              {errorHandler(error)}
            </Alert>
          ) : isSuccess ? (
            tasks?.length === 0 ? (
              <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                <Typography variant='body1'>There are no tasks at the moment </Typography>
              </Alert>
            ) : (
              <>
                {studendView ? (
                  <TaskCarouselView tasks={tasks} />
                ) : (
                  <DraggableGrid
                    items={tasks}
                    handleCache={handleUpdateCache}
                    queryCacheName='classroomListClassroomTasks'
                  />
                )}
              </>
            )
          ) : null}
        </>
      )}
      {activeButton === 'ispeak-tasks' && (
        <>
          {isLoadingIspeak ? (
            Array(3)
              .fill(null)
              .map((elem, index) => <Skeleton height={160} key={index} />)
          ) : isErrorIspeak ? (
            <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              {errorHandler(errorIspeak)}
            </Alert>
          ) : isSuccessIspeak ? (
            ispeakTasks?.length === 0 ? (
              <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                <Typography variant='body1'>There are no tasks at the moment </Typography>
              </Alert>
            ) : (
              <>
                {studendView ? (
                  <TaskCarouselView tasks={ispeakTasks} />
                ) : (
                  <DraggableGrid
                    items={ispeakTasks}
                    queryCacheName='classroomGetIspeakTasks'
                    handleCache={handleUpdateCache}
                  />
                )}
              </>
            )
          ) : null}
        </>
      )}
      {activeButton === 'live-practice' && (
        <Grid container spacing={2}>
          {isLoadinglivePractice ? (
            Array(3)
              .fill(null)
              .map((elem, index) => <Skeleton height={160} key={index} />)
          ) : isErrorlivePractice ? (
            <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              {errorHandler(errorlivePractice)}
            </Alert>
          ) : isSuccesslivePractice ? (
            livePracticeTask === null ? (
              <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                <Typography variant='body1'>There are no tasks at the moment </Typography>
              </Alert>
            ) : (
              <>
                {studendView ? (
                  <CardActionArea
                    onClick={() => {
                      window.open(
                        'https://us06web.zoom.us/j/84761252159?pwd=QTBGZk9ZMnRUV1BjV28ySG1jNFYzQT09',
                        '_blank',
                      )
                    }}
                  >
                    <Task note={livePracticeTask} />
                  </CardActionArea>
                ) : (
                  <Grid item xs={3}>
                    <LivePracticeCard task={livePracticeTask} is_ai={false} />
                  </Grid>
                )}
              </>
            )
          ) : null}
        </Grid>
      )}
      {activeButton !== 'my-tasks' &&
        activeButton !== 'ispeak-tasks' &&
        activeButton !== 'live-practice' && (
          <>
            {isLoading ? (
              Array(3)
                .fill(null)
                .map((elem, index) => <Skeleton height={160} key={index} />)
            ) : isError ? (
              <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                {errorHandler(error)}
              </Alert>
            ) : isSuccess ? (
              selectedFolder?.tasks?.length === 0 ? (
                <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                  <Typography variant='body1'>There are no tasks at the moment </Typography>
                </Alert>
              ) : (
                <>
                  {studendView ? (
                    <TaskCarouselView tasks={selectedFolder?.tasks} />
                  ) : (
                    <DraggableGrid
                      items={selectedFolder?.tasks}
                      queryCacheName='classroomGetTeacherTaskFolders'
                      handleCache={handleUpdateCacheFolder}
                    />
                  )}
                </>
              )
            ) : null}
          </>
        )}
    </>
  )
}

interface NoteCardNoteProps {
  task: ClassroomTaskResponse
  is_ai: boolean
  listeners?: any
  attributes?: any
  isDragging?: boolean
  style?: any
  withOpacity?: boolean
}

const NoteCard = React.forwardRef<HTMLDivElement, NoteCardNoteProps>((props, ref) => {
  const { task, is_ai } = props
  const { open, handleClose, handleOpen } = useToggle(false)
  return (
    <>
      <Card
        ref={ref}
        sx={{
          // p:4,
          mx: 'auto',
          position: 'relative',
          width: '100%',
          maxWidth: '270px',
          height: '150px',
          transformOrigin: '50% 50%',
          background: task?.color ?? '#FFF0C8',
          opacity: props.withOpacity ? '0.5' : '1',
          transform: props.isDragging ? 'scale(1.05)' : 'scale(1)',
          ...props.style,
        }}
        elevation={1}
      >
        {!is_ai && (
          <MenuNote
            task={task}
            isDragging={props.isDragging}
            attributes={props.attributes}
            listeners={props.listeners}
          />
        )}
        {is_ai && (
          <MenuIspeakNote
            task={task}
            isDragging={props.isDragging}
            attributes={props.attributes}
            listeners={props.listeners}
          />
        )}
        <VideoIcon videoUrl={task?.video_link} />
        <CardActionArea onClick={handleOpen}>
          <Box
            sx={{
              p: 1,
              overflow: 'clip',
              fontSize: '12px',
              color: '#000',
              width: '100%',
              height: '200px',
              marginTop: '20px',
              wordBreak: 'break-word',
            }}
          >
            {task.questions.map((question) => {
              return parse(question.text)
            })}
          </Box>
        </CardActionArea>
      </Card>
      <ModalDisplayNote task={task} open={open} onClose={handleClose} is_ai={is_ai} />
    </>
  )
})
NoteCard.displayName = 'NoteCard'

const SortableNoteCard = (props) => {
  const { isDragging, attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.index,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  }
  return (
    <NoteCard
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
      withOpacity={isDragging}
      attributes={attributes}
      listeners={listeners}
      {...props}
    />
  )
}

const LivePracticeCard: React.FC<NoteCardNoteProps> = (props) => {
  const { task } = props
  const { open, handleClose, handleOpen } = useToggle(false)

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          width: '100%',
          height: '150px',
          background: task?.color ?? '#FFF0C8',
        }}
        elevation={1}
      >
        <CardActionArea onClick={handleOpen}>
          <Box
            sx={{
              p: 1,
              overflow: 'clip',
              fontSize: '12px',
              color: '#000',
              width: '100%',
              height: '200px',
            }}
          >
            {task.questions.map((question) => {
              return parse(question.text)
            })}
          </Box>
        </CardActionArea>
      </Card>
      <ModalDisplayNote task={task} open={open} onClose={handleClose} is_ai={true} />
    </>
  )
}

type DraggableGridProps = {
  items: object[]
  queryCacheName: string
  handleCache?: (queryCacheName: string, orderArray: any) => void
}

const DraggableGrid = (props: DraggableGridProps) => {
  const [items, setItems] = React.useState([])
  const [activeId, setActiveId] = React.useState<number | null>(null)
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))
  const [orderTasks] = useClassroomOrderClassroomTasksMutation()
  const SortableNoteCardMemoized = React.memo(SortableNoteCard)
  const getItemByIndex = React.useCallback((index: number) => {
    return items.find((item) => item.idx === index)
  })

  React.useEffect(() => {
    setItems(props.items.map((item, index) => ({ idx: index + 1, item: item })))
  }, [props.items])

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = React.useCallback(async (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.idx === active.id)
        const newIndex = items.findIndex((item) => item.idx === over!.id)

        const newArray = arrayMove(items, oldIndex, newIndex)
        orderTasks({
          classroomTasksOrderRequest: {
            task_ordering: newArray.map((item, idx) => {
              return {
                task_id: item.item.id,
                order: idx,
              }
            }),
          },
        })
        props?.handleCache(props.queryCacheName, newArray)
        return newArray
      })
    }

    setActiveId(null)
  }, [])

  const handleDragCancel = React.useCallback(() => {
    setActiveId(null)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items.map((item) => item.idx)} strategy={rectSortingStrategy}>
        <Grid container spacing={2}>
          {items?.map((item, index) => (
            <Grid item xs={12} sm={4} md={3} key={index}>
              <SortableNoteCardMemoized task={item.item} is_ai={false} index={item.idx} />
            </Grid>
          ))}
        </Grid>
      </SortableContext>
      <DragOverlay>
        {activeId !== null ? (
          <NoteCard task={getItemByIndex(activeId).item} is_ai={false} isDragging />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

const VideoIcon = (props: { videoUrl?: string }) => {
  if (!props?.videoUrl) {
    return null
  }
  return (
    <Box sx={{ position: 'absolute', top: '5px', right: '50%', transform: 'translate(50%,0%)' }}>
      {props?.videoUrl.includes('shorts') ? (
        <svg
          width='25'
          height='23'
          viewBox='0 0 25 23'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clipPath='url(#clip0_405_92)'>
            <path
              d='M16.0424 0.507504C18.971 -0.624158 22.5969 0.200231 24.1382 2.34926C25.6796 4.4983 24.5551 7.15696 21.6266 8.28862L19.2186 9.2273C21.2931 9.28351 23.2715 10.1285 24.3075 11.5731C25.8489 13.7221 24.727 16.3807 21.7959 17.5124L8.95478 22.515C6.02624 23.6466 2.4003 22.8222 0.858967 20.6732C-0.682372 18.5242 0.442047 15.8655 3.37059 14.7338L5.77862 13.7952C3.70413 13.739 1.72565 12.894 0.689672 11.4494C-0.851666 9.30037 0.272753 6.64171 3.2013 5.51005L16.0424 0.507504ZM9.11144 7.96823L16.8838 11.5262L9.11144 15.0673V7.96823Z'
              fill='#F40407'
            />
            <path d='M9.11157 7.96045V15.0525L16.884 11.5149L9.11157 7.96045Z' fill='white' />
          </g>
          <defs>
            <clipPath id='clip0_405_92'>
              <rect width='25' height='23' fill='white' />
            </clipPath>
          </defs>
        </svg>
      ) : (
        <svg
          width='29'
          height='20'
          viewBox='0 0 29 20'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 1.78814e-07 14.285 0 14.285 0C14.285 0 5.35042 1.78814e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C1.78814e-07 5.35042 0 10 0 10C0 10 1.78814e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z'
            fill='#FF0000'
          />
          <path d='M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z' fill='white' />
        </svg>
      )}
    </Box>
  )
}
export default DisplayNotes
